import {
    SETTINGS_COMPANY_ADDRESSES,
    SETTINGS_COMPANY_CHECKOUT_BUTTON_REQUEST_STATUS,
    SETTINGS_COMPANY_DESCRIPTION_SLUG,
    SETTINGS_COMPANY_EMAILS,
    SETTINGS_COMPANY_FAVICON_SLUG,
    SETTINGS_COMPANY_HIDE_NON_DELEGATE_DOMAIN_SETUP,
    SETTINGS_COMPANY_HOME_SLUG,
    SETTINGS_COMPANY_INN_CONFIRMED,
    SETTINGS_COMPANY_INVERTED_LOGO_SLUG,
    SETTINGS_COMPANY_LEGAL_NAME_SLUG,
    SETTINGS_COMPANY_LOGO_SLUG,
    SETTINGS_COMPANY_NAME_SLUG,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ALIAS,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_EMAIL,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ENABLED,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_PASSWORD,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_POSTBOX_EMAIL,
    SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_SENDING_METHOD,
    SETTINGS_COMPANY_PHONES,
    SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_ALIAS,
    SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_EMAIL,
    SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_PASSWORD,
    SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_POSTBOX_EMAIL,
    SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_SENDING_METHOD,
    SETTINGS_COMPANY_RCCT_SLUG,
    SETTINGS_COMPANY_SOCIAL_NETWORKS_SLUG,
    SETTINGS_COMPANY_TITLE_SLUG,
    SETTINGS_COMPANY_TRUTHFUL_LABELS_ENABLED,
    SETTINGS_COMPANY_WORKING_MODES,
    SETTINGS_COMPANY_YANDEX_METRIKA_ID,
} from '../../constants';
import { type SettingsValue, useSettingsFactory } from './useSettingsFactory';

export type SettingsCompanyName = SettingsValue<typeof SETTINGS_COMPANY_NAME_SLUG>;
export type SettingsCompanyDescription = SettingsValue<typeof SETTINGS_COMPANY_DESCRIPTION_SLUG>;
export type SettingsCompanyLogo = SettingsValue<typeof SETTINGS_COMPANY_LOGO_SLUG>;
export type SettingsCompanyInvertedLogo = SettingsValue<typeof SETTINGS_COMPANY_INVERTED_LOGO_SLUG>;
export type SettingsCompanyFavicon = SettingsValue<typeof SETTINGS_COMPANY_FAVICON_SLUG>;
export type SettingsCompanyInformation =
    | SettingsValue<typeof SETTINGS_COMPANY_WORKING_MODES>
    | SettingsValue<typeof SETTINGS_COMPANY_PHONES>
    | SettingsValue<typeof SETTINGS_COMPANY_EMAILS>
    | SettingsValue<typeof SETTINGS_COMPANY_ADDRESSES>;
export type SettingsCompanySocialNetworks = SettingsValue<typeof SETTINGS_COMPANY_SOCIAL_NETWORKS_SLUG>;
export type SettingsCompanyOrderEmailNotificationEnabled = SettingsValue<
    typeof SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ENABLED
>;
export type SettingsCompanyNotificationEmail =
    | SettingsValue<typeof SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_EMAIL>
    | SettingsValue<typeof SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_EMAIL>;
export type SettingsCompanyOrderEmailNotificationPostboxEmail = SettingsValue<
    typeof SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_POSTBOX_EMAIL
>;
export type SettingsCompanyOrderNotificationSendingMethod = SettingsValue<
    typeof SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_SENDING_METHOD
>;
export type SettingsCompanyPromoNotificationSendingMethod = SettingsValue<
    typeof SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_SENDING_METHOD
>;
export type SettingsCompanyPromoNotificationPostboxEmail = SettingsValue<
    typeof SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_POSTBOX_EMAIL
>;
export type SettingsCompanyOrderNotificationAlias = SettingsValue<
    typeof SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ALIAS
>;
export type SettingsCompanyPromoNotificationAlias = SettingsValue<
    typeof SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_ALIAS
>;

export type SettingsCompanyNotificationSendingMethod =
    | SettingsCompanyOrderNotificationSendingMethod
    | SettingsCompanyPromoNotificationSendingMethod;

export type SettingsCompanyNotificationAlias =
    | SettingsCompanyOrderNotificationAlias
    | SettingsCompanyPromoNotificationAlias;

export const useSettingsCompany = () =>
    useSettingsFactory({
        companyTitle: { path: SETTINGS_COMPANY_TITLE_SLUG },
        companyName: { path: SETTINGS_COMPANY_NAME_SLUG },
        companyLegalName: { path: SETTINGS_COMPANY_LEGAL_NAME_SLUG },
        companyDescription: { path: SETTINGS_COMPANY_DESCRIPTION_SLUG },
        companyLogo: { path: SETTINGS_COMPANY_LOGO_SLUG },
        companyInvertedLogo: { path: SETTINGS_COMPANY_INVERTED_LOGO_SLUG },
        companyFavicon: { path: SETTINGS_COMPANY_FAVICON_SLUG },
        companyRCCT: { path: SETTINGS_COMPANY_RCCT_SLUG },
        companyHome: { path: SETTINGS_COMPANY_HOME_SLUG },
        companyPhones: { path: SETTINGS_COMPANY_PHONES },
        companyAddresses: { path: SETTINGS_COMPANY_ADDRESSES },
        companyEmails: { path: SETTINGS_COMPANY_EMAILS },
        companyOrderEmailNotificationEnabled: { path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ENABLED },
        companyOrderEmailNotificationEmail: { path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_EMAIL },
        companyOrderEmailNotificationPostboxEmail: { path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_POSTBOX_EMAIL },
        companyOrderEmailNotificationAlias: { path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_ALIAS },
        companyOrderEmailNotificationPassword: { path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_PASSWORD },
        companyPromoEmailNotificationEmail: { path: SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_EMAIL },
        companyPromoEmailNotificationPostboxEmail: { path: SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_POSTBOX_EMAIL },
        companyPromoEmailNotificationAlias: { path: SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_ALIAS },
        companyPromoEmailNotificationPassword: { path: SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_PASSWORD },
        companyWorkingModes: { path: SETTINGS_COMPANY_WORKING_MODES },
        companySocialNetworks: { path: SETTINGS_COMPANY_SOCIAL_NETWORKS_SLUG },
        companyYandexMetrikaId: { path: SETTINGS_COMPANY_YANDEX_METRIKA_ID },
        truthfulLabelsEnabled: { path: SETTINGS_COMPANY_TRUTHFUL_LABELS_ENABLED },
        companyInnConfirmed: { path: SETTINGS_COMPANY_INN_CONFIRMED },
        hideNonDelegateDomainSetup: {
            path: SETTINGS_COMPANY_HIDE_NON_DELEGATE_DOMAIN_SETUP,
        },
        checkoutButtonRequestStatus: {
            path: SETTINGS_COMPANY_CHECKOUT_BUTTON_REQUEST_STATUS,
        },
        companyOrderEmailNotificationSendingMethod: {
            path: SETTINGS_COMPANY_ORDER_EMAIL_NOTIFICATION_SENDING_METHOD,
        },
        companyPromoEmailNotificationSendingMethod: {
            path: SETTINGS_COMPANY_PROMO_EMAIL_NOTIFICATION_SENDING_METHOD,
        },
    });
